import type { StudioId, TopupPlan } from '@/domain/types';

/**
 * Ops reports (tv-4cn), owner view. The well-defined half: overdue top-ups the
 * desk is still sitting on (money at risk). The "Ops performance" half is a
 * first-pass snapshot — count + total outstanding — pending a product decision
 * on the exact performance metric.
 */
export interface OverdueTopup {
  planId: string;
  customerId: string;
  studioId: StudioId;
  /** Instalments still owed on the plan. */
  outstandingAud: number;
  /** The oldest past-due missed instalment — the age signal. */
  oldestMissedIso: string;
}

export interface OpsOverdueReport {
  rows: OverdueTopup[];
  count: number;
  totalOutstandingAud: number;
}

/** Active top-up plans with a past-due missed instalment, in scope, oldest first. */
export function opsOverdueTopups(args: {
  nowIso: string;
  topupPlans: Record<string, TopupPlan>;
  studioIds: StudioId[];
}): OpsOverdueReport {
  const { nowIso, topupPlans, studioIds } = args;
  const inScope = (sid: StudioId) => studioIds.length === 0 || studioIds.includes(sid);
  const now = new Date(nowIso).getTime();

  const rows: OverdueTopup[] = [];
  for (const plan of Object.values(topupPlans)) {
    if (plan.status !== 'active' || !inScope(plan.studioId)) continue;
    const missedPastDue = plan.instalments.filter(
      (i) => i.status === 'missed' && new Date(i.dueIso).getTime() < now,
    );
    if (missedPastDue.length === 0) continue;
    const outstandingAud = plan.instalments
      .filter((i) => i.status !== 'paid')
      .reduce((sum, i) => sum + i.amountAud, 0);
    const oldestMissedIso = missedPastDue.map((i) => i.dueIso).sort()[0]!;
    rows.push({
      planId: plan.id,
      customerId: plan.customerId,
      studioId: plan.studioId,
      outstandingAud,
      oldestMissedIso,
    });
  }
  rows.sort((a, b) => a.oldestMissedIso.localeCompare(b.oldestMissedIso));

  return {
    rows,
    count: rows.length,
    totalOutstandingAud: rows.reduce((sum, r) => sum + r.outstandingAud, 0),
  };
}
